import React from 'react';
import { Dialog as PrimeDialog } from 'primereact/dialog';
import { TrackNowButton } from '../ui/TrackNowButton';
import { SuccessIllustration, WarningIllustration, ErrorIllustration, QuestionIllustration, DangerConfirmIllustration } from './DialogIllustrations';
import './CustomDialog.css';
import WarningAmberIcon from "@mui/icons-material/WarningAmber";


const CustomDialog = ({ header, children, visible, onHide, footer, onConfirm, confirmLabel = 'Yes', cancelLabel = 'No', icon, compact = false, className, dialogType, ...props }) => {
  const extractText = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string' || typeof value === 'number') return String(value);
    if (Array.isArray(value)) return value.map((item) => extractText(item)).join(' ');
    if (React.isValidElement(value)) return extractText(value.props?.children);
    return String(value);
  };

  const autoDetectType = (msg) => {
    if (!msg) return 'info';
    const lower = extractText(msg).toLowerCase();
    if (/(error|failed|wrong|invalid|cannot|unable|not found|not able)/i.test(lower)) return 'error';
    if (/(file does not support)/i.test(lower)) return 'error';
    if (/(please select|please choose|required|must|should)/i.test(lower)) return 'error';
    if (/(success|successfully|saved|completed|done|created|updated|confirmed|approved|rejected)/i.test(lower)) return 'success';
    if (/(confirm|sure|proceed|are you sure|are you certain)/i.test(lower)) return 'confirm';
    if (/(warning|caution)/i.test(lower)) return 'warning';
    return 'info';
  };

  const message = extractText(children).replace(/\s+/g, ' ').trim();
  const type = autoDetectType(message);
  const isConfirmDialog = onConfirm !== undefined;
  const resolvedType = dialogType || (isConfirmDialog ? 'confirm' : type);
  const lowerMessage = message.toLowerCase();
  const isDeleteLike = /(delete|remove)/i.test(lowerMessage);
  const isDeferLike = /(defer|deferred)/i.test(lowerMessage);

  const confirmActionLabel = (() => {
    if (confirmLabel !== 'Yes') return confirmLabel;
    if (isDeleteLike) return 'Yes, Delete!';
    if (isDeferLike) return 'Yes, Defer';
    return 'Yes, Continue';
  })();

  const cancelActionLabel = (() => {
    if (cancelLabel !== 'No') return cancelLabel;
    if (isDeleteLike || isDeferLike) return 'No, Keep It.';
    return 'Cancel';
  })();

  const getIllustration = () => {
    if (icon) {
      return <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>{icon}</div>;
    }
    switch (resolvedType) {
      case 'success': return <SuccessIllustration />;
      case 'warning': return <WarningIllustration />;
      case 'error': return <ErrorIllustration />;
      case 'info':
        return (
          <span style={{ fontSize: '48px', lineHeight: 1 }} role="img" aria-label="info">
            ⚠️
          </span>
        );      // case 'confirm': return <QuestionIllustration />;
      // default: return <WarningIllustration />;
      case 'confirm': return <DangerConfirmIllustration />;
      default: return <QuestionIllustration />;
    }
  };

  const getTitle = () => {
    //console.log("getTitle header", header, "resolvedType", resolvedType);

    if (header && header !== "Info" && header !== "Information") return header;
    if (/(please select single|please select a single)/i.test(message)) return 'Multiple files selected!';
    if (/(please select more than one)/i.test(message)) return 'Single file selected!';
    switch (resolvedType) {
      case 'success': return 'Success!';
      case 'warning': return 'Selection Required';
      case 'error': return 'Wrong!';
      case 'confirm': return 'Confirmation';
      case 'info': return 'Info';
      default: return 'Selection Required';
    }
  };

  const customFooter = footer || (
    isConfirmDialog ? (
      <div className="dialog-buttons confirm-actions">
        <TrackNowButton variant="tertiary" onClick={onHide}>
          {cancelActionLabel}
        </TrackNowButton>
        <TrackNowButton
          variant={(isDeleteLike || isDeferLike) ? "destructive" : "primary"}
          onClick={onConfirm}
        >
          {confirmActionLabel}
        </TrackNowButton>
      </div>
    ) : (
      <div className="dialog-buttons">
        <TrackNowButton variant="primary" onClick={onHide}>
          OK
        </TrackNowButton>
      </div>
    )
  );

  // Calculate dynamic width based on message length
  const messageLength = typeof message === 'string' ? message.length : 0;
  let dialogWidth = isConfirmDialog ? '560px' : '400px';
  if (!isConfirmDialog) {
    if (messageLength > 100) dialogWidth = '500px';
    if (messageLength > 200) dialogWidth = '600px';
    if (messageLength > 300) dialogWidth = '700px';
  }

  return (
    <PrimeDialog
      visible={visible}
      onHide={onHide}
      footer={customFooter}
      className={`custom-dialog ${resolvedType === 'confirm' ? 'custom-dialog--confirm' : ''} ${compact ? 'custom-dialog--compact' : ''} ${className || ''}`}
      closable={false}
      style={{
        width: dialogWidth,
        minWidth: isConfirmDialog ? "560px" : "400px",
        maxWidth: "90vw"
      }}
      {...props}
    >
      <div className="dialog-illustration">
        {getIllustration()}
      </div>
      <h2 className={`dialog-title ${resolvedType}`}>{getTitle()}</h2>
      <div className={`dialog-message${/(file does not support)/i.test(message) ? ' dialog-message--compact' : ''}`}>
        {children}
      </div>
    </PrimeDialog>
  );
};

export default CustomDialog;
